import React, { forwardRef, useMemo } from 'react';
import { useImperativeHandle } from 'react';
import { Form } from 'react-final-form';
import styled from 'styled-components';

import { Survey, SurveyQuestion, SurveyResponse } from 'apis/merchant-gateway/nps/types';

import MatrixQuestion from './MatrixQuestion';
import OpenEndedQuestion from './OpenEndedQuestion';

type SurveyMonkeyQuestionnaireProps = {
  survey: Survey;
  onSubmit: (values: object) => void;
  validate?: (values: any) => object | undefined;
};

const QuestionContainer = styled.div`
  &:not(:first-of-type) {
    margin-block-start: 24px;
  }
`;

const renderQuestion = (question: SurveyQuestion, pageIdx: number, questionIdx: number) => {
  switch (question.family) {
    case 'matrix':
      return <MatrixQuestion question={question} pageIdx={pageIdx} questionIdx={questionIdx} />;
    case 'open_ended':
      return <OpenEndedQuestion question={question} pageIdx={pageIdx} questionIdx={questionIdx} />;
  }
};

export type SubmittableSurveyForm = {
  submitForm(): void;
};

const SurveyMonkeyQuestionnaire = forwardRef<SubmittableSurveyForm, SurveyMonkeyQuestionnaireProps>(
  ({ survey, onSubmit }, formRef) => {
    let formSubmitFunc: () => void;
    useImperativeHandle(formRef, () => ({
      submitForm() {
        formSubmitFunc && formSubmitFunc();
      },
    }));

    const initialValues = useMemo<SurveyResponse>(() => {
      return {
        pages: survey.pages.map(page => ({
          id: page.id,
          questions: page.questions.map(question => {
            let answers = null;
            if (question.family === 'matrix') {
              answers = question.answers?.rows.map(row => ({ rowId: row.id, choiceId: '' }));
            }
            if (question.family === 'open_ended') {
              answers = [{ text: '' }];
            }
            return {
              id: question.id,
              family: question.family,
              answers,
            };
          }),
        })),
      } as SurveyResponse;
    }, [survey]);

    return (
      <Form onSubmit={onSubmit} initialValues={initialValues}>
        {({ handleSubmit }) => {
          formSubmitFunc = handleSubmit;
          return survey.pages.map((page, pageIdx) => (
            <form onSubmit={handleSubmit} key={page.id}>
              {page.questions.map((question, questionIdx) => (
                <QuestionContainer key={question.id}>
                  {renderQuestion(question, pageIdx, questionIdx)}
                </QuestionContainer>
              ))}
            </form>
          ));
        }}
      </Form>
    );
  },
);

export default SurveyMonkeyQuestionnaire;
